import React from "react";
import {
  buildDispatchDayCalendarControlsFromPayload,
  loadDispatchDayContextPayload,
  normalizeDispatchMonthKey,
  resetDispatchDayContextCache,
  resolveDispatchDayCalendarOpenMonthKey,
} from "../utils/dispatchDayCalendarContext.js";

export {
  buildDispatchDayCalendarControlsFromPayload,
  loadDispatchDayContextPayload,
  resetDispatchDayContextCache,
  resolveDispatchDayCalendarOpenMonthKey,
} from "../utils/dispatchDayCalendarContext.js";

export default function useDispatchDayCalendarContext({
  activeDate = "",
  forceReloadOnOpen = false,
  isOpen = false,
} = {}) {
  const [payload, setPayload] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showArchive, setShowArchive] = React.useState(false);
  const [activeMonthKey, setActiveMonthKey] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isCancelled = false;

    setIsLoading(true);
    setErrorMessage("");

    void loadDispatchDayContextPayload({ forceReload: forceReloadOnOpen })
      .then((nextPayload) => {
        if (isCancelled) {
          return;
        }

        setPayload(nextPayload);
        setIsLoading(false);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        setIsLoading(false);
        setErrorMessage(error instanceof Error ? error.message : "Impossible de charger les journées de dispatch.");
      });

    return () => {
      isCancelled = true;
    };
  }, [forceReloadOnOpen, isOpen]);

  const controls = React.useMemo(
    () => buildDispatchDayCalendarControlsFromPayload(payload, {
      activeDate,
      showArchive,
    }),
    [activeDate, payload, showArchive],
  );
  const defaultDispatchDate = React.useMemo(() => {
    const payloadDefault = typeof payload?.default_dispatch_date === "string"
      ? payload.default_dispatch_date.trim()
      : typeof payload?.defaultDispatchDate === "string"
        ? payload.defaultDispatchDate.trim()
        : "";
    if (payloadDefault) {
      return payloadDefault;
    }
    return Array.isArray(controls?.items) && controls.items.length > 0
      ? String(controls.items[0]?.dispatchDate || "").trim()
      : "";
  }, [controls, payload]);

  const openMonthKey = React.useMemo(
    () => resolveDispatchDayCalendarOpenMonthKey({
      activeDate,
      controls,
    }),
    [activeDate, controls],
  );
  const previousIsOpenRef = React.useRef(false);
  const previousOpenMonthKeyRef = React.useRef("");

  React.useEffect(() => {
    const justOpened = isOpen && !previousIsOpenRef.current;
    const openMonthKeyBecameAvailable = isOpen
      && Boolean(openMonthKey)
      && !previousOpenMonthKeyRef.current;

    previousIsOpenRef.current = isOpen;
    previousOpenMonthKeyRef.current = openMonthKey;

    if (!isOpen) {
      if (openMonthKey) {
        setActiveMonthKey(openMonthKey);
      }
      return;
    }

    if (openMonthKey && (justOpened || openMonthKeyBecameAvailable)) {
      setActiveMonthKey(openMonthKey);
    }
  }, [isOpen, openMonthKey]);

  const handleSelectMonthKey = React.useCallback((monthKey) => {
    const safeMonthKey = normalizeDispatchMonthKey(monthKey);
    if (!safeMonthKey) {
      return;
    }

    setActiveMonthKey(safeMonthKey);
  }, []);

  const handleToggleArchive = React.useCallback((nextShowArchive) => {
    setShowArchive(nextShowArchive === true);
  }, []);

  return {
    activeMonthKey,
    controls,
    defaultDispatchDate,
    errorMessage,
    handleSelectMonthKey,
    handleToggleArchive,
    isLoading,
    loadingMessage: isLoading ? "Chargement des journées de dispatch..." : "",
    showArchive,
  };
}
